<template>
  <div class="account-dropdown-root">
    <CDropdown
      inNav
      class="c-header-nav-items account-dropdown"
      placement="bottom-end"
      add-menu-classes="pt-0 account-dropdown__menu"
    >
      <template #toggler>
        <CHeaderNavLink>
          <div class="c-avatar account-avatar" :title="userDisplayName">
            <img
              v-if="userPhotoUrl"
              :src="userPhotoUrl"
              :alt="userDisplayName || 'User photo'"
              class="account-avatar__img"
              @error="onPhotoError"
            />
            <span v-else class="account-avatar__initials">{{ userInitials }}</span>
          </div>
        </CHeaderNavLink>
      </template>

      <div class="account-dropdown__header">
        <span class="account-dropdown__avatar">
          <img
            v-if="userPhotoUrl"
            :src="userPhotoUrl"
            :alt="userDisplayName || 'User photo'"
            class="account-dropdown__avatar-img"
            @error="onPhotoError"
          />
          <span v-else>{{ userInitials }}</span>
        </span>
        <span class="account-dropdown__identity">
          <span class="account-dropdown__name">{{ userDisplayName || 'Account' }}</span>
          <span v-if="userEmail" class="account-dropdown__email">{{ userEmail }}</span>
        </span>
      </div>

      <ul class="account-dropdown__list">
        <li class="account-dropdown__item" @click="goToProfileSettings()">
          <span class="account-dropdown__icon">
            <CIcon name="cil-user"/>
          </span>
          <span class="account-dropdown__label">Profile Settings</span>
        </li>
        <li class="account-dropdown__item" @click="goToNetworkSpeed()">
          <span class="account-dropdown__icon">
            <CIcon name="cil-speedometer"/>
          </span>
          <span class="account-dropdown__label">Network Speed</span>
        </li>
        <li class="account-dropdown__divider" aria-hidden="true"/>
        <li class="account-dropdown__item account-dropdown__item--logout" data-cta-sound="on" @click="openLogoutModal()">
          <span class="account-dropdown__icon">
            <CIcon name="cil-lock-locked"/>
          </span>
          <span class="account-dropdown__label">Logout</span>
        </li>
      </ul>
    </CDropdown>

    <transition name="lo-fade">
      <div
        v-if="showLogoutModal"
        ref="logoutModal"
        class="lo-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="logout-modal-title"
        tabindex="-1"
        @mousedown.self="onBackdropMousedown"
      >
        <div class="lo-panel" role="document" @mousedown.stop>
          <div class="lo-icon-wrap">
            <span class="lo-icon-ring lo-icon-ring--1" aria-hidden="true"></span>
            <span class="lo-icon-ring lo-icon-ring--2" aria-hidden="true"></span>
            <span class="lo-icon-orb" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path class="lo-icon-door" d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline class="lo-icon-arrow" points="16 17 21 12 16 7"/>
                <line class="lo-icon-line" x1="21" y1="12" x2="9" y2="12"/>
              </svg>
            </span>
          </div>

          <div class="lo-body">
            <h3 id="logout-modal-title" class="lo-title">Sign out of your account?</h3>
            <p class="lo-message">
              <template v-if="userDisplayName">
                You'll be signed out as <strong>{{ userDisplayName }}</strong> and returned to the login screen.
              </template>
              <template v-else>
                You'll be signed out and returned to the login screen.
              </template>
            </p>
          </div>

          <div class="lo-actions">
            <button
              type="button"
              class="lo-btn lo-btn--ghost"
              :disabled="isLoggingOut"
              @click="closeLogoutModal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="lo-btn lo-btn--danger"
              :class="{ 'lo-btn--busy': isLoggingOut }"
              :disabled="isLoggingOut"
              @click="confirmLogout"
              data-cta-sound="on"
            >
              <span v-if="isLoggingOut" class="lo-spinner" aria-hidden="true"></span>
              <span>{{ isLoggingOut ? 'Signing out…' : 'Yes, sign out' }}</span>
            </button>
          </div>
        </div>
      </div>
    </transition>

    <transition name="gb-fade">
      <div v-if="showGoodbye" ref="goodbyeSplash" class="gb-splash" role="status" aria-live="polite">
        <div class="gb-stage">
          <div class="gb-burst" aria-hidden="true">
            <span class="gb-ring gb-ring--1"></span>
            <span class="gb-ring gb-ring--2"></span>
            <span class="gb-orb">
              <svg viewBox="0 0 24 24" width="44" height="44" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path class="gb-door" d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline class="gb-arrow" points="16 17 21 12 16 7"/>
                <line class="gb-line" x1="21" y1="12" x2="9" y2="12"/>
              </svg>
            </span>
          </div>
          <h1 class="gb-title">
            <span class="gb-title__lead">See you soon</span>
            <template v-if="goodbyeName">
              <span class="gb-title__comma">,</span>
              <span class="gb-title__name">{{ goodbyeName }}</span>
            </template>
            <span class="gb-title__bang">!</span>
          </h1>
          <p class="gb-subtitle">Signing you out…</p>
          <div class="gb-progress" aria-hidden="true">
            <span class="gb-progress__bar"></span>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios'
import posthog from 'posthog-js'
import { resetCompletion } from '@/utils/profileCompletion'

export default {
  name: 'TheHeaderDropdownAccnt',
  data() {
    return {
      photoBroken: false,
      showLogoutModal: false,
      isLoggingOut: false,
      showGoodbye: false,
      goodbyeName: '',
    }
  },
  watch: {
    showLogoutModal(now) {
      if (now) {
        document.addEventListener('keydown', this.onLogoutKeydown)
        this._prevBodyOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        this.$nextTick(() => {
          const root = this.$refs.logoutModal
          if (root && root.parentNode !== document.body) {
            document.body.appendChild(root)
          }
          if (root) root.focus()
        })
      } else {
        document.removeEventListener('keydown', this.onLogoutKeydown)
        if (this._prevBodyOverflow !== undefined) {
          document.body.style.overflow = this._prevBodyOverflow
          this._prevBodyOverflow = undefined
        }
      }
    },
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onLogoutKeydown)
    if (this._prevBodyOverflow !== undefined) {
      document.body.style.overflow = this._prevBodyOverflow
      this._prevBodyOverflow = undefined
    }
    const root = this.$refs.logoutModal
    if (root && root.parentNode === document.body) {
      document.body.removeChild(root)
    }
    const splash = this.$refs.goodbyeSplash
    if (splash && splash.parentNode === document.body) {
      document.body.removeChild(splash)
    }
    if (this._goodbyeTimer) {
      clearTimeout(this._goodbyeTimer)
      this._goodbyeTimer = null
    }
  },
  computed: {
    authUser() {
      try {
        const raw = localStorage.getItem('auth_user')
        return raw ? JSON.parse(raw) : null
      } catch (error) {
        return null
      }
    },
    userDisplayName() {
      const user = this.authUser
      if (!user) {
        return ''
      }

      const name = typeof user.name === 'string' ? user.name.trim() : ''
      if (name) {
        return name
      }

      const email = typeof user.email === 'string' ? user.email.trim() : ''
      return email
    },
    userEmail() {
      const user = this.authUser
      if (!user) {
        return ''
      }

      const email = typeof user.email === 'string' ? user.email.trim() : ''
      if (!email) {
        return ''
      }

      const name = typeof user.name === 'string' ? user.name.trim() : ''
      return name ? email : ''
    },
    userPhotoUrl() {
      if (this.photoBroken) {
        return ''
      }
      const user = this.authUser
      const image = user && typeof user.image === 'string' ? user.image.trim() : ''
      return image
    },
    userInitials() {
      const user = this.authUser
      const name = user && typeof user.name === 'string' ? user.name.trim() : ''

      if (name) {
        const parts = name.split(/\s+/).filter(Boolean).slice(0, 2)
        if (parts.length > 0) {
          return parts.map((part) => part[0].toUpperCase()).join('')
        }
      }

      const email = user && typeof user.email === 'string' ? user.email.trim() : ''
      if (email) {
        return email[0].toUpperCase()
      }

      return '?'
    },
  },
  methods: {
    onPhotoError() {
      this.photoBroken = true
    },
    goToProfileSettings() {
      if (this.$route.path === '/profile-settings') return
      this.$router.push({ path: '/profile-settings' })
    },
    goToNetworkSpeed() {
      if (this.$route.path === '/network-speed') return
      this.$router.push({ path: '/network-speed' })
    },
    goToOldOms() {
      window.open('https://raymas.manakkalagropharma.com', '_blank', 'noopener')
    },
    pickGoodbyeName(user) {
      if (!user) return ''
      const name = typeof user.name === 'string' ? user.name.trim() : ''
      if (name) {
        const first = name.split(/\s+/)[0]
        return first || name
      }
      const email = typeof user.email === 'string' ? user.email.trim() : ''
      if (email) {
        return email.split('@')[0]
      }
      return ''
    },
    clearAuthStateAndRedirect() {
      localStorage.removeItem('api_token')
      localStorage.removeItem('roles')
      localStorage.removeItem('auth_user')
      resetCompletion()
      this.$store.commit('clearOrganisationContext')
      this.$store.commit('clearPermissions')
      posthog.reset()
      this.$router.push({ path: '/login' })
    },
    runGoodbyeAndRedirect() {
      this.goodbyeName = this.pickGoodbyeName(this.authUser)
      this.showLogoutModal = false
      this.showGoodbye = true
      this.$nextTick(() => {
        const root = this.$refs.goodbyeSplash
        if (root && root.parentNode !== document.body) {
          document.body.appendChild(root)
        }
      })
      this._goodbyeTimer = setTimeout(() => {
        this.clearAuthStateAndRedirect()
      }, 2200)
    },
    openLogoutModal() {
      this.showLogoutModal = true
    },
    closeLogoutModal() {
      if (this.isLoggingOut) return
      this.showLogoutModal = false
    },
    onBackdropMousedown() {
      this.closeLogoutModal()
    },
    onLogoutKeydown(event) {
      if (event.key === 'Escape' && !this.isLoggingOut) {
        event.preventDefault()
        this.closeLogoutModal()
      }
    },
    confirmLogout() {
      if (this.isLoggingOut) return
      this.isLoggingOut = true
      const self = this

      axios.post(this.$apiAdress + '/api/logout', {})
        .then(function () {
          self.runGoodbyeAndRedirect()
        }).catch(function (error) {
          console.log(error)
          self.runGoodbyeAndRedirect()
        })
    }
  }
}
</script>

<style scoped>
.account-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.2);
  overflow: hidden;
}

.account-avatar__initials {
  font-size: 0.82rem;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: 0.02em;
  line-height: 1;
}

.account-avatar__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.account-dropdown ::v-deep .account-dropdown__menu {
  width: 260px;
  padding: 0;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.12);
  overflow: hidden;
}

.account-dropdown__header {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.85rem 1rem;
  background: #fbfcfe;
  border-bottom: 1px solid #e5e9f2;
}

.account-dropdown__header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
}

.account-dropdown__avatar {
  flex: 0 0 auto;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.18);
  overflow: hidden;
}

.account-dropdown__avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.account-dropdown__identity {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.account-dropdown__name {
  font-size: 0.84rem;
  font-weight: 600;
  color: #1f2a44;
  letter-spacing: 0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.account-dropdown__email {
  font-size: 0.7rem;
  font-weight: 500;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.account-dropdown__list {
  list-style: none;
  margin: 0;
  padding: 0.35rem 0;
}

.account-dropdown__item {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.6rem 1rem;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;
}

.account-dropdown__item:hover {
  background: #f7f9fc;
}

.account-dropdown__icon {
  flex: 0 0 auto;
  width: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  transition: color 0.12s ease;
}

.account-dropdown__icon ::v-deep .c-icon {
  width: 16px;
  height: 16px;
}

.account-dropdown__label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #1f2a44;
  letter-spacing: 0.01em;
}

.account-dropdown__divider {
  height: 1px;
  margin: 0.25rem 0;
  background: #e5e9f2;
  list-style: none;
}

.account-dropdown__item--logout:hover {
  background: rgba(220, 38, 38, 0.06);
}

.account-dropdown__item--logout:hover .account-dropdown__icon,
.account-dropdown__item--logout:hover .account-dropdown__label {
  color: #b91c1c;
}
</style>

<style>
/* Unscoped so styles persist when the panel is teleported to <body>. */
.lo-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  outline: none;
}

.lo-panel {
  position: relative;
  width: 100%;
  max-width: 420px;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 16px;
  box-shadow:
    0 24px 60px rgba(15, 23, 42, 0.32),
    0 2px 8px rgba(15, 23, 42, 0.08);
  padding: 1.6rem 1.4rem 1.2rem;
  overflow: hidden;
  transform-origin: center 30%;
}

.lo-panel::before {
  content: '';
  position: absolute;
  inset: 0 0 auto 0;
  height: 4px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  transform-origin: left center;
  animation: lo-bar-slide 0.55s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.lo-icon-wrap {
  position: relative;
  width: 76px;
  height: 76px;
  margin: 0.25rem auto 0.95rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.lo-icon-orb {
  position: relative;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #b91c1c;
  box-shadow:
    0 6px 16px rgba(220, 38, 38, 0.22),
    inset 0 0 0 1px rgba(220, 38, 38, 0.12);
  z-index: 2;
  animation: lo-orb-pop 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}

.lo-icon-ring {
  position: absolute;
  top: 50%;
  left: 50%;
  border-radius: 50%;
  border: 2px solid rgba(220, 38, 38, 0.45);
  transform: translate(-50%, -50%) scale(0.6);
  opacity: 0;
  z-index: 1;
}

.lo-icon-ring--1 {
  width: 76px;
  height: 76px;
  animation: lo-ring-pulse 2s ease-out 0.35s infinite;
}

.lo-icon-ring--2 {
  width: 76px;
  height: 76px;
  border-color: rgba(220, 38, 38, 0.32);
  animation: lo-ring-pulse 2s ease-out 1.1s infinite;
}

.lo-icon-door {
  transform-origin: 9px 12px;
  animation: lo-door-swing 1.6s ease-in-out 0.4s both;
}

.lo-icon-arrow,
.lo-icon-line {
  stroke-dasharray: 22;
  stroke-dashoffset: 22;
  animation: lo-stroke-draw 0.6s ease-out 0.55s forwards;
}

.lo-icon-arrow {
  animation-delay: 0.65s;
}

.lo-body {
  text-align: center;
  padding: 0 0.25rem;
}

.lo-title {
  margin: 0 0 0.45rem;
  font-size: 1.08rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.3;
  animation: lo-rise 0.45s cubic-bezier(0.22, 1, 0.36, 1) 0.15s both;
}

.lo-message {
  margin: 0;
  font-size: 0.85rem;
  color: #4b5563;
  line-height: 1.5;
  animation: lo-rise 0.45s cubic-bezier(0.22, 1, 0.36, 1) 0.25s both;
}

.lo-message strong {
  color: #1f2a44;
  font-weight: 600;
}

.lo-actions {
  display: flex;
  gap: 0.55rem;
  margin-top: 1.25rem;
  animation: lo-rise 0.45s cubic-bezier(0.22, 1, 0.36, 1) 0.35s both;
}

.lo-btn {
  flex: 1 1 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  height: 40px;
  padding: 0 1rem;
  border-radius: 10px;
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  border: 1px solid transparent;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.lo-btn:disabled {
  cursor: not-allowed;
  opacity: 0.75;
}

.lo-btn:focus { outline: none; }
.lo-btn::-moz-focus-inner { border: 0; }

.lo-btn--ghost {
  background: #ffffff;
  border-color: #e5e9f2;
  color: #1f2a44;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.lo-btn--ghost:hover:not(:disabled) {
  border-color: #b9c2d0;
  background: #f8fafc;
  transform: translateY(-1px);
}

.lo-btn--ghost:focus-visible {
  border-color: #16a07a;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.lo-btn--danger {
  background: linear-gradient(135deg, #ef4444 0%, #b91c1c 100%);
  border-color: transparent;
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(185, 28, 28, 0.28);
}

.lo-btn--danger:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 8px 18px rgba(185, 28, 28, 0.36);
  filter: brightness(1.04);
}

.lo-btn--danger:active:not(:disabled) {
  transform: translateY(0);
  filter: brightness(0.96);
}

.lo-btn--danger:focus-visible {
  box-shadow: 0 4px 12px rgba(185, 28, 28, 0.28), 0 0 0 3px rgba(220, 38, 38, 0.32);
}

.lo-btn--busy {
  cursor: progress;
}

.lo-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: lo-spin 0.7s linear infinite;
}

/* Enter / leave transition for the overlay + panel */
.lo-fade-enter-active,
.lo-fade-leave-active {
  transition: opacity 0.22s ease;
}

.lo-fade-enter-active .lo-panel {
  animation: lo-panel-in 0.42s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}

.lo-fade-leave-active .lo-panel {
  transition: transform 0.18s ease, opacity 0.18s ease;
}

.lo-fade-enter,
.lo-fade-leave-to {
  opacity: 0;
}

.lo-fade-leave-to .lo-panel {
  transform: translateY(6px) scale(0.97);
  opacity: 0;
}

@keyframes lo-panel-in {
  0% {
    opacity: 0;
    transform: translateY(24px) scale(0.85);
  }
  60% {
    opacity: 1;
    transform: translateY(-4px) scale(1.015);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes lo-bar-slide {
  0%   { transform: scaleX(0); }
  100% { transform: scaleX(1); }
}

@keyframes lo-orb-pop {
  0%   { transform: scale(0.3) rotate(-12deg); opacity: 0; }
  60%  { transform: scale(1.08) rotate(4deg); opacity: 1; }
  100% { transform: scale(1) rotate(0); opacity: 1; }
}

@keyframes lo-ring-pulse {
  0%   { transform: translate(-50%, -50%) scale(0.6); opacity: 0.7; }
  80%  { transform: translate(-50%, -50%) scale(1.35); opacity: 0; }
  100% { transform: translate(-50%, -50%) scale(1.35); opacity: 0; }
}

@keyframes lo-door-swing {
  0%   { transform: rotate(0deg); }
  35%  { transform: rotate(-22deg); }
  70%  { transform: rotate(-10deg); }
  100% { transform: rotate(0deg); }
}

@keyframes lo-stroke-draw {
  0%   { stroke-dashoffset: 22; opacity: 0.4; }
  100% { stroke-dashoffset: 0;  opacity: 1; }
}

@keyframes lo-rise {
  0%   { opacity: 0; transform: translateY(8px); }
  100% { opacity: 1; transform: translateY(0); }
}

@keyframes lo-spin {
  to { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
  .lo-panel::before,
  .lo-icon-orb,
  .lo-icon-ring--1,
  .lo-icon-ring--2,
  .lo-icon-door,
  .lo-icon-arrow,
  .lo-icon-line,
  .lo-title,
  .lo-message,
  .lo-actions,
  .lo-fade-enter-active .lo-panel {
    animation: none !important;
  }
}

@media (max-width: 480px) {
  .lo-overlay {
    padding: 0.75rem;
    align-items: flex-end;
  }

  .lo-panel {
    max-width: 100%;
    border-radius: 16px;
  }

  .lo-actions {
    flex-direction: column-reverse;
    gap: 0.6rem;
  }

  .lo-btn {
    flex: 0 0 auto;
    width: 100%;
    height: 44px;
    font-size: 0.9rem;
  }
}

/* ---------------- Goodbye splash (logout) ---------------- */
.gb-splash {
  position: fixed;
  inset: 0;
  z-index: 2100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background: linear-gradient(135deg, #7f1d1d 0%, #b91c1c 50%, #ef4444 100%);
  color: #ffffff;
  overflow: hidden;
}

.gb-splash::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 20% 25%, rgba(255, 255, 255, 0.16), transparent 45%),
    radial-gradient(circle at 80% 75%, rgba(255, 255, 255, 0.10), transparent 50%);
  pointer-events: none;
  animation: gb-glow 5s ease-in-out infinite alternate;
}

@keyframes gb-glow {
  0%   { opacity: 0.7; transform: scale(1); }
  100% { opacity: 1;   transform: scale(1.06); }
}

.gb-stage {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  z-index: 1;
}

.gb-burst {
  position: relative;
  width: 120px;
  height: 120px;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gb-ring {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.55);
  transform: translate(-50%, -50%) scale(0.6);
  opacity: 0;
}

.gb-ring--1 { animation: gb-ring-pulse 2.2s ease-out 0.2s infinite; }
.gb-ring--2 { animation: gb-ring-pulse 2.2s ease-out 1.1s infinite; }

@keyframes gb-ring-pulse {
  0%   { transform: translate(-50%, -50%) scale(0.6); opacity: 0.85; }
  80%  { transform: translate(-50%, -50%) scale(1.4); opacity: 0; }
  100% { transform: translate(-50%, -50%) scale(1.4); opacity: 0; }
}

.gb-orb {
  position: relative;
  z-index: 2;
  width: 84px;
  height: 84px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  color: #b91c1c;
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.22),
    inset 0 0 0 1px rgba(185, 28, 28, 0.08);
  animation: gb-orb-pop 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}

@keyframes gb-orb-pop {
  0%   { transform: scale(0.3) rotate(-15deg); opacity: 0; }
  60%  { transform: scale(1.08) rotate(4deg);  opacity: 1; }
  100% { transform: scale(1) rotate(0);        opacity: 1; }
}

.gb-door {
  transform-origin: 9px 12px;
  animation: gb-door-swing 1.6s ease-in-out 0.5s both;
}

.gb-arrow,
.gb-line {
  stroke-dasharray: 22;
  stroke-dashoffset: 22;
  animation: gb-stroke-draw 0.55s ease-out 0.55s forwards;
}

.gb-arrow {
  animation-delay: 0.7s;
}

@keyframes gb-door-swing {
  0%   { transform: rotate(0deg); }
  35%  { transform: rotate(-22deg); }
  70%  { transform: rotate(-10deg); }
  100% { transform: rotate(0deg); }
}

@keyframes gb-stroke-draw {
  0%   { stroke-dashoffset: 22; opacity: 0.4; }
  100% { stroke-dashoffset: 0;  opacity: 1; }
}

.gb-title {
  margin: 0;
  font-size: clamp(1.6rem, 3.6vw, 2.2rem);
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #ffffff;
  line-height: 1.2;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: center;
  gap: 0.25rem;
}

.gb-title__lead,
.gb-title__comma,
.gb-title__name,
.gb-title__bang {
  display: inline-block;
  opacity: 0;
  transform: translateY(8px);
  animation: gb-rise 0.45s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}

.gb-title__lead  { animation-delay: 0.30s; }
.gb-title__comma { animation-delay: 0.40s; }
.gb-title__name  { animation-delay: 0.45s; color: #fecaca; }
.gb-title__bang  { animation-delay: 0.55s; }

.gb-subtitle {
  margin: 0.6rem 0 0;
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.82);
  letter-spacing: 0.02em;
  opacity: 0;
  transform: translateY(8px);
  animation: gb-rise 0.45s cubic-bezier(0.22, 1, 0.36, 1) 0.65s forwards;
}

@keyframes gb-rise {
  to { opacity: 1; transform: translateY(0); }
}

.gb-progress {
  position: relative;
  width: 220px;
  max-width: 60vw;
  height: 3px;
  margin-top: 1.6rem;
  background: rgba(255, 255, 255, 0.18);
  border-radius: 999px;
  overflow: hidden;
  opacity: 0;
  animation: gb-fade-in 0.4s ease-out 0.7s forwards;
}

.gb-progress__bar {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 35%;
  border-radius: 999px;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, #ffffff 50%, rgba(255, 255, 255, 0) 100%);
  animation: gb-progress-slide 1.1s ease-in-out 0.8s infinite;
}

@keyframes gb-progress-slide {
  0%   { left: -40%; }
  100% { left: 100%; }
}

@keyframes gb-fade-in {
  to { opacity: 1; }
}

.gb-fade-enter-active,
.gb-fade-leave-active {
  transition: opacity 0.3s ease;
}

.gb-fade-enter,
.gb-fade-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .gb-splash::before,
  .gb-ring,
  .gb-orb,
  .gb-door,
  .gb-arrow,
  .gb-line,
  .gb-title__lead,
  .gb-title__comma,
  .gb-title__name,
  .gb-title__bang,
  .gb-subtitle,
  .gb-progress,
  .gb-progress__bar {
    animation: none !important;
  }
  .gb-title__lead,
  .gb-title__comma,
  .gb-title__name,
  .gb-title__bang,
  .gb-subtitle,
  .gb-progress {
    opacity: 1;
    transform: none;
  }
}
</style>
